import React, { useState } from 'react';
import { X } from 'lucide-react';
import type { VendorCreatePayload } from '../../../utils/vendorsApi';
import { useEscapeKey } from '../../../hooks/useEscapeKey';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (payload: VendorCreatePayload) => Promise<void>;
};

export const CreateVendorModal: React.FC<Props> = ({ isOpen, onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [vendorCode, setVendorCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEscapeKey(onClose, isOpen && !saving);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (!name.trim()) throw new Error('Vendor name is required');
      await onCreate({
        name: name.trim(),
        vendor_code: vendorCode.trim() || null,
      });
      onClose();
      setName('');
      setVendorCode('');
    } catch (e: any) {
      setError(e?.message || 'Failed to create vendor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/20 dark:bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg bg-white dark:bg-dark-bg-secondary rounded-2xl shadow-xl border border-gray-200 dark:border-dark-border overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">Add Vendor</h3>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center text-gray-500 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg-hover"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && <div className="text-sm text-red-600">{error}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Vendor Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="block w-full rounded-md border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="Covetrus"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Vendor Code (optional)</label>
            <input
              value={vendorCode}
              onChange={(e) => setVendorCode(e.target.value)}
              className="block w-full rounded-md border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="COV-123"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-border flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-dark-border rounded-md text-sm font-medium text-gray-700 dark:text-dark-text-secondary bg-white dark:bg-dark-bg-tertiary hover:bg-gray-50 dark:hover:bg-dark-bg-hover"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save Vendor'}
          </button>
        </div>
      </div>
    </div>
  );
};


